import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';
import SocketService from '../../../../packages/shared/socket';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';
import ScreenHeader from '../components/ScreenHeader';
import ScreenNavigation from '../components/ScreenNavigation';

const ScreenContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  overflow: hidden;
  position: relative;
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  z-index: 500;
`;


const Stats = styled.div`
  position: absolute;
  top: 80px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 15px;
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  z-index: 100;
`;

const SVGContainer = styled.svg`
  width: 100%;
  height: 100%;
`;

const ControlPanel = styled.div`
  position: absolute;
  top: 80px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 15px;
  border-radius: 10px;
  z-index: 100;
`;

const GroupButton = styled.button`
  display: block;
  width: 100%;
  padding: 8px 15px;
  margin: 5px 0;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

// Mock comment data generator
const generateMockComments = () => {
  const topics = [
    { text: "Climate change needs urgent action", category: "environment", sentiment: "urgent" },
    { text: "We should invest in renewable energy", category: "environment", sentiment: "positive" },
    { text: "Urban planning must consider sea level rise", category: "urban", sentiment: "concerned" },
    { text: "Digital transformation is reshaping work", category: "technology", sentiment: "neutral" },
    { text: "AI could help solve complex problems", category: "technology", sentiment: "positive" },
    { text: "Education systems need fundamental reform", category: "social", sentiment: "urgent" },
    { text: "Healthcare accessibility is a human right", category: "social", sentiment: "positive" },
    { text: "Economic inequality is growing", category: "economic", sentiment: "concerned" },
    { text: "Remote work changes city dynamics", category: "urban", sentiment: "neutral" },
    { text: "Carbon tax could drive behavior change", category: "environment", sentiment: "positive" },
    { text: "Mental health support is critical", category: "social", sentiment: "urgent" },
    { text: "Blockchain for transparent governance", category: "technology", sentiment: "neutral" },
    { text: "Food security concerns are rising", category: "social", sentiment: "concerned" },
    { text: "Green buildings reduce emissions", category: "environment", sentiment: "positive" },
    { text: "Public transport needs investment", category: "urban", sentiment: "urgent" },
    { text: "Data privacy laws need updating", category: "technology", sentiment: "concerned" },
    { text: "Universal basic income worth exploring", category: "economic", sentiment: "neutral" },
    { text: "Community gardens build resilience", category: "urban", sentiment: "positive" },
    { text: "Circular economy reduces waste", category: "environment", sentiment: "positive" },
    { text: "Skills retraining programs essential", category: "economic", sentiment: "urgent" }
  ];
  
  return topics.map((topic, i) => ({
    id: `comment_${i}`,
    text: topic.text,
    author: `Participant ${Math.floor(Math.random() * 20) + 1}`,
    timestamp: new Date(Date.now() - Math.random() * 3600000).toISOString(),
    category: topic.category,
    sentiment: topic.sentiment,
    keywords: topic.text.toLowerCase().split(' ').filter(w => w.length > 4),
    influence: Math.random()
  }));
};

export function PostItScreen({ screenMode = false }) {
  // Handle screen navigation commands (disabled in screenMode - ScreenClient handles navigation)
  useScreenNavigation({ screenMode });
  const svgRef = useRef();
  const [comments, setComments] = useState([]);
  const [groupBy, setGroupBy] = useState('category');
  const [simulation, setSimulation] = useState(null);
  const [screenInfo, setScreenInfo] = useState({ screenId: '', sessionId: '' });
  
  useEffect(() => {
    // Get pairing information from localStorage
    const pairedScreenId = localStorage.getItem('pairedScreenId');
    const sessionId = localStorage.getItem('sessionId') || 'default';

    if (pairedScreenId) {
      setScreenInfo({ screenId: pairedScreenId, sessionId });
      
      const socket = SocketService.socket;

      if (socket) {
        // Set up socket listeners
        socket.on('screen-command', (command) => {
          console.log('📺 PostItScreen received command:', command);
          
          // Handle screen-specific commands (navigation handled by useScreenNavigation hook)
          if (command.command === 'group-by' && command.payload.groupBy) {
            setGroupBy(command.payload.groupBy);
          } else if (command.command === 'add-comment' && command.payload.comment) {
            setComments(prev => [...prev, command.payload.comment]);
          } else if (command.command === 'clear-comments') {
            setComments([]);
          }
        });
        
        socket.on('new-submission', (data) => {
          console.log('📝 New submission received:', data);
          // Convert submission to post-it comment format
          if (data.submission && data.submission.content) {
            const newComment = {
              id: Date.now(),
              text: data.submission.content,
              category: data.submission.category || 'general',
              sentiment: data.submission.sentiment || 'neutral',
              author: data.participantId || 'Anonymous',
              timestamp: new Date().toISOString()
            };
            setComments(prev => [...prev, newComment]);
          }
        });
        
        // Send screen ready status
        socket.emit('screen-status', {
          screenId: pairedScreenId,
          status: 'ready',
          currentView: 'postit'
        });
        
      } else {
        console.warn('⚠️ PostItScreen: No socket found, this should not happen after pairing');
      }
    }
    
    // Generate initial comments
    const mockComments = generateMockComments();
    setComments(mockComments);
    
    // Simulate new comments arriving
    const interval = setInterval(() => {
      const newComment = {
        id: `comment_${Date.now()}`,
        text: `New idea at ${new Date().toLocaleTimeString()}`,
        author: `Participant ${Math.floor(Math.random() * 20) + 1}`,
        timestamp: new Date().toISOString(),
        category: ['environment', 'technology', 'social', 'urban', 'economic'][Math.floor(Math.random() * 5)],
        sentiment: ['positive', 'neutral', 'concerned', 'urgent'][Math.floor(Math.random() * 4)],
        keywords: ['innovation', 'future', 'change'],
        influence: Math.random()
      };
      
      setComments(prev => [...prev.slice(-29), newComment]);
    }, 8000);
    
    // Cleanup function
    return () => {
      clearInterval(interval);
      
      // Clean up socket listeners if they exist
      const socket = SocketService.socket;
      if (socket) {
        socket.off('screen-command');
        socket.off('new-submission');
      }
    };
  }, []);
  
  // Send periodic screen state updates for preview
  useEffect(() => {
    const pairedScreenId = localStorage.getItem('pairedScreenId');
    
    if (pairedScreenId && SocketService.socket && SocketService.socket.connected) {
      const updateInterval = setInterval(() => {
        if (SocketService.socket && SocketService.socket.connected) {
          SocketService.socket.emit('screen-state-update', {
            screenId: pairedScreenId,
            view: 'postit',
            stats: {
              commentCount: comments.length,
              categories: [...new Set(comments.map(c => c.category))].length,
              grouping: groupBy
            },
            preview: {
              type: 'postit',
              backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              title: '🗨️ Virtual Post-it Organizer'
            }
          });
        }
      }, 2000); // Update every 2 seconds
      
      return () => clearInterval(updateInterval);
    }
  }, [comments, groupBy]);
  
  useEffect(() => {
    if (!comments.length) return;
    
    const svg = d3.select(svgRef.current);
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Clear previous elements
    svg.selectAll('*').remove();
    
    // Define color scales
    const categoryColors = {
      environment: '#10B981',
      technology: '#3B82F6', 
      social: '#F59E0B',
      urban: '#8B5CF6',
      economic: '#EF4444'
    };
    
    const sentimentOpacity = {
      positive: 0.9,
      neutral: 0.7,
      concerned: 0.6,
      urgent: 1.0
    };
    
    // Create container for zoom
    const container = svg.append('g');
    
    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        container.attr('transform', event.transform);
      });
    
    svg.call(zoom);
    
    // Calculate cluster centers based on grouping
    const getClusterCenters = () => {
      const centers = {};
      
      if (groupBy === 'category') {
        const categories = [...new Set(comments.map(c => c.category))];
        categories.forEach((cat, i) => {
          centers[cat] = {
            x: (width / (categories.length + 1)) * (i + 1),
            y: height / 2
          };
        });
      } else if (groupBy === 'sentiment') {
        const sentiments = [...new Set(comments.map(c => c.sentiment))];
        sentiments.forEach((sent, i) => {
          centers[sent] = {
            x: width / 2,
            y: (height / (sentiments.length + 1)) * (i + 1)
          };
        });
      } else {
        centers.all = { x: width / 2, y: height / 2 };
      }
      
      return centers;
    };
    
    const centers = getClusterCenters();
    
    // Create force simulation
    const newSimulation = d3.forceSimulation(comments)
      .force('charge', d3.forceManyBody().strength(-50))
      .force('collision', d3.forceCollide().radius(d => 30 + d.influence * 20))
      .force('center', d3.forceCenter(width / 2, height / 2));
    
    // Add clustering force
    if (groupBy !== 'none') {
      newSimulation.force('cluster', d3.forceX(d => {
        const key = groupBy === 'category' ? d.category : d.sentiment;
        return centers[key]?.x || width / 2;
      }).strength(0.3));
      
      newSimulation.force('clusterY', d3.forceY(d => {
        const key = groupBy === 'category' ? d.category : d.sentiment;
        return centers[key]?.y || height / 2;
      }).strength(0.3));
    }
    
    // Draw cluster backgrounds
    if (groupBy !== 'none') {
      const clusterGroups = container.append('g').attr('class', 'clusters');
      
      Object.entries(centers).forEach(([key, center]) => {
        clusterGroups.append('circle')
          .attr('cx', center.x)
          .attr('cy', center.y)
          .attr('r', 150)
          .attr('fill', categoryColors[key] || '#999')
          .attr('opacity', 0.1);
        
        clusterGroups.append('text')
          .attr('x', center.x)
          .attr('y', center.y - 120)
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', '1.2rem')
          .attr('font-weight', '300')
          .attr('opacity', 0.7)
          .text(key.charAt(0).toUpperCase() + key.slice(1));
      });
    }
    
    // Create comment groups
    const commentGroups = container.append('g')
      .attr('class', 'comments')
      .selectAll('g')
      .data(comments)
      .enter()
      .append('g')
      .attr('class', 'comment-group')
      .call(d3.drag()
        .on('start', function(event, d) {
          if (!event.active) newSimulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', function(event, d) {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', function(event, d) {
          if (!event.active) newSimulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));
    
    // Add circles (post-it backgrounds)
    commentGroups.append('circle')
      .attr('r', d => 30 + d.influence * 20)
      .attr('fill', d => categoryColors[d.category] || '#666')
      .attr('opacity', d => sentimentOpacity[d.sentiment] || 0.7)
      .attr('stroke', 'white')
      .attr('stroke-width', 2);
    
    // Add text (truncated)
    commentGroups.append('text')
      .attr('text-anchor', 'middle')
      .attr('fill', 'white')
      .attr('font-size', '0.8rem')
      .attr('pointer-events', 'none')
      .selectAll('tspan')
      .data(d => {
        const words = d.text.split(' ');
        const lines = [];
        let current = [];
        
        words.forEach(word => {
          if (current.join(' ').length + word.length > 20) {
            lines.push(current.join(' '));
            current = [word];
          } else {
            current.push(word);
          }
        });
        if (current.length) lines.push(current.join(' '));
        
        return lines.slice(0, 3);
      })
      .enter()
      .append('tspan')
      .attr('x', 0)
      .attr('dy', (d, i) => i === 0 ? '-0.5em' : '1em')
      .text(d => d);
    
    // Add hover effects
    commentGroups
      .on('mouseenter', function(event, d) {
        d3.select(this).select('circle')
          .transition()
          .duration(200)
          .attr('r', d => 35 + d.influence * 20)
          .attr('opacity', 1);
        
        // Show full text tooltip
        const tooltip = container.append('g').attr('class', 'tooltip');
        
        tooltip.append('rect')
          .attr('x', d.x - 100)
          .attr('y', d.y - 60)
          .attr('width', 200)
          .attr('height', 40)
          .attr('fill', 'black')
          .attr('opacity', 0.8)
          .attr('rx', 5);
        
        tooltip.append('text')
          .attr('x', d.x)
          .attr('y', d.y - 40)
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', '0.9rem')
          .text(d.text);
      })
      .on('mouseleave', function(event, d) {
        d3.select(this).select('circle')
          .transition()
          .duration(200)
          .attr('r', d => 30 + d.influence * 20)
          .attr('opacity', d => sentimentOpacity[d.sentiment] || 0.7);
        
        container.selectAll('.tooltip').remove();
      });
    
    // Update positions on simulation tick
    newSimulation.on('tick', () => {
      commentGroups.attr('transform', d => `translate(${d.x}, ${d.y})`);
    });
    
    setSimulation(newSimulation);
    
    // Cleanup
    return () => {
      if (newSimulation) newSimulation.stop();
    };
  }, [comments, groupBy]);
  
  const stats = {
    total: comments.length,
    categories: [...new Set(comments.map(c => c.category))].length,
    participants: [...new Set(comments.map(c => c.author))].length
  };
  
  return (
    <ScreenContainer>
      <ScreenHeader 
        title={<ScreenNavigation currentScreenTitle="Post-It Screen" />}
        subtitle="Collect and organize participant feedback"
        transparent={true}
        kuutioOpacity={0.9}
      />
      <Header>
        {screenInfo.screenId && (
          <div style={{ fontSize: '0.8rem', marginTop: '5px', opacity: 0.7, textAlign: 'center' }}>
            🖥️ Screen: {screenInfo.screenId} | Session: {screenInfo.sessionId}
          </div>
        )}
      </Header>
      
      <Stats>
        <div>💬 Comments: {stats.total}</div>
        <div>🏷️ Categories: {stats.categories}</div>
        <div>👥 Participants: {stats.participants}</div>
        <div style={{ marginTop: '10px', fontSize: '0.8rem', opacity: 0.8 }}>
          {screenInfo.screenId ? '✅ Paired' : '⚠️ Not paired'}
        </div>
      </Stats>
      
      <ControlPanel>
        <div style={{ color: 'white', marginBottom: '10px', fontSize: '0.9rem' }}>Group By:</div>
        <GroupButton 
          active={groupBy === 'category'} 
          onClick={() => setGroupBy('category')}
        >
          Category
        </GroupButton>
        <GroupButton 
          active={groupBy === 'sentiment'} 
          onClick={() => setGroupBy('sentiment')}
        >
          Sentiment
        </GroupButton>
        <GroupButton 
          active={groupBy === 'none'} 
          onClick={() => setGroupBy('none')}
        >
          No Grouping
        </GroupButton>
      </ControlPanel>
      
      <SVGContainer ref={svgRef} />
    </ScreenContainer>
  );
}

export default PostItScreen;